import React, { useEffect, useRef, useState } from "react";
import { Card, CardBody } from "@progress/kendo-react-layout";
import "./css/Dashboard.scss";
import "./css/Common.scss";
import { Grid, GridCellProps, GridColumn } from "@progress/kendo-react-grid";
import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { chevronDownIcon, eyeIcon } from "@progress/kendo-svg-icons";
import { useNavigate } from "react-router-dom";
import { Label } from "@progress/kendo-react-labels";
import {
  Chart,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartSeriesLabels,
} from "@progress/kendo-react-charts";
import Breadcrumbs from "./components/Breadcrumbs";
import axios from "axios";
import {
  commonBaseUrl,
  commonChartColor,
  RightAlignedHeaderCell,
} from "../services/CommonUtil";
import { process, State } from "@progress/kendo-data-query";
import { DayView, Scheduler } from "@progress/kendo-react-scheduler";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";

interface ViewCommandCellProps extends GridCellProps {
  enterView: (item: any) => void;
}

interface ViewPaymentCellProps extends GridCellProps {
  paymentView: (item: any) => void;
}

const initialDataState: State = {
  take: 5,
  skip: 0,
  group: [],
};

interface Duration {
  durationName: string;
  actualRevenue: number;
  targetedRevenue: number;
}

interface RevenueCountList {
  [spaceName: string]: Duration[];
}

export const Dashboard = () => {
  // Set initial state for the dynamic data
  const baseURL = commonBaseUrl();

  const navigate = useNavigate();
  const hasFetched = useRef(false);

  const [allCount, setAllCount] = useState({
    totalBookings: 0,
    totalSpaces: 0,
    checkInCount: 0,
    pendingPayments: 0,
    totalRevenue: 69620,
    outstandingAmount: 0,
  });
  const [allRevenueCountList, setAllRevenueCountList] =
    useState<RevenueCountList>();
  const [allCheckedList, setAllCheckedList] = useState([]);
  const [allOccupancyList, setAllOccupancyList] = useState([]);
  const [allPaymentList, setAllPaymentList] = useState([]);
  const [allOutstandingList, setAllOutstandingList] = useState({
    success: true,
    outstandingPrice: 0.0,
    dueAmount: 0.0,
  });
  const [allInvoiceList, setAllInvoiceList] = useState<any>();
  const [selected, setSelected] = useState<string>("today");
  const [dataState, setDataState] = useState<State>(initialDataState);

  // YYYY-MM-DD format conversion
  const formatDate = (date: any) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Breadcrums
  const breadCrumbsData = [
    {
      id: "2",
      text: "Dashboard",
      route: "",
    },
  ];

  useEffect(() => {
    // Prevent second call
    if (hasFetched.current) return;
    hasFetched.current = true;
    handleTodayClick();
    allRevenueCount();
    getSpaceTypeAPI();
  });

  // Initial API call
  const allRevenueCount = async () => {
    const currentDate = new Date();
    const startOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    );
    const endOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0
    );

    try {
      const response = await axios.get(
        `${baseURL}GetTargetedRevenue?startDate=${formatDate(
          startOfMonth
        )}&endDate=${formatDate(endOfMonth)}`
      );
      const groupedData: RevenueCountList = response.data.content.reduce(
        (acc: any, item: any) => {
          if (!acc[item.spaceName]) {
            acc[item.spaceName] = [];
          }
          acc[item.spaceName].push(item);
          return acc;
        },
        {}
      );
      setAllRevenueCountList(groupedData);
    } catch (err) {
      alert(err);
    }
  };

  const allDashboardCount = async (fromDate: any, toDate: any) => {
    try {
      const response = await axios.get(
        `${baseURL}GetDashboardCount?bookingFrom=${fromDate}&bookingTo=${toDate}`
      );
      setAllCount(response.data);
      allCheckInAPI(fromDate, toDate);
    } catch (err) {
      alert(err);
    }
  };

  const allCheckInAPI = async (fromDate: any, toDate: any) => {
    try {
      const response = await axios.get(
        `${baseURL}GetAllCheckInCustomers?bookingFrom=${fromDate}&bookingTo=${toDate}`
      );
      setAllCheckedList(response.data);
      console.log(response.data.length);
      allOccupancyGraphAPI(fromDate, toDate);
    } catch (err) {
      alert(err);
    }
  };

  const allOccupancyGraphAPI = async (fromDate: any, toDate: any) => {
    try {
      const response = await axios.get(
        `${baseURL}GetDashboardOccupancy?bookingFrom=${fromDate}&bookingTo=${toDate}`
      );
      const occupancyFilteredData = response.data?.filter(
        (space: any) => space.percentage !== 0.0
      );
      setAllOccupancyList(occupancyFilteredData);
      allPaymentGraphAPI(fromDate, toDate);
    } catch (err) {
      alert(err);
    }
  };

  const allPaymentGraphAPI = async (fromDate: any, toDate: any) => {
    try {
      const response = await axios.get(
        `${baseURL}GetAllPayments?bookingFrom=${fromDate}&bookingTo=${toDate}`
      );
      setAllPaymentList(response.data.reverse().slice(0, 5));
      allInvoiceAPI(fromDate, toDate);
    } catch (err) {
      alert(err);
    }
  };

  const allInvoiceAPI = async (fromDate: any, toDate: any) => {
    try {
      const response = await axios.get(
        `${baseURL}GetDashboardInvoices?bookingFrom=${fromDate}&bookingTo=${toDate}`
      );
      const invoiceGraph = [];
      let totalData = {};
      let pendingData = {};
      if (response.data.totalPaid > 0) {
        totalData = {
          category: "Paid",
          percentage: response.data.totalPaid,
        };
      }
      if (response.data.totalPending > 0) {
        pendingData = {
          category: "Pending",
          percentage: response.data.totalPending,
        };
      }
      invoiceGraph.push(totalData);
      invoiceGraph.push(pendingData);
      setAllInvoiceList(invoiceGraph);
      allOutstandingAPI(fromDate, toDate);
    } catch (err) {
      alert(err);
    }
  };

  const allOutstandingAPI = async (fromDate: any, toDate: any) => {
    try {
      const response = await axios.get(
        `${baseURL}GetTotalOutstandingAmount?startDate=${fromDate}&endDate=${toDate}`
      );
      setAllOutstandingList(response.data);
    } catch (err) {
      alert(err);
    }
  };
  //

  // Grid View Option
  const MyViewCommandCell = (props: ViewCommandCellProps) => (
    <ViewCommandCell {...props} />
  );

  const ViewCommandCell = (props: ViewCommandCellProps) => {
    return (
      <td>
        <Button
          className="view-color"
          type="button"
          svgIcon={eyeIcon}
          onClick={() => props.enterView(props.dataItem)}
        ></Button>
      </td>
    );
  };

  const enterView = (item: any) => {
    for (let d = 0; d < allCheckedList.length; d++) {
      if (item.bookingId === allCheckedList[d]["bookingId"]) {
        getBookingData(allCheckedList[d]["bookingId"], true);
        break;
      }
    }
  };

  // Booking API
  const getBookingData = async (id: any, view: any) => {
    try {
      const response = await axios.get(` ${baseURL}odata/Bookings(${id})`);
      navigate("/viewbooking", {
        state: { infoId: response.data, view },
      });
    } catch (err) {}
  };

  const ViewDashboardPaymentCell = (props: ViewPaymentCellProps) => (
    <ViewPaymentCell {...props} />
  );

  const ViewPaymentCell = (props: ViewPaymentCellProps) => {
    return (
      <td>
        <Button
          className="view-color"
          type="button"
          svgIcon={eyeIcon}
          onClick={() => props.paymentView(props.dataItem)}
        ></Button>
      </td>
    );
  };

  const paymentView = (item: any) => {
    console.log("The item was -->", item);
    for (let d = 0; d < allPaymentList.length; d++) {
      if (item.bookingId === allPaymentList[d]["bookingId"]) {
        getPaymentsData(allPaymentList[d]["bookingId"], true);
        break;
      }
    }
  };

  // Payment API
  const getPaymentsData = async (id: any, view: any) => {
    try {
      const response = await axios.get(`${baseURL}GetInvoiceHeader`);
      for (let d = 0; d < response.data.length; d++) {
        if (id === response.data[d]["bookingId"]) {
          navigate("/viewpayment", {
            state: {
              invoiceId: response.data[d]["id"],
              bookingId: response.data[d]["bookingId"],
              view: true,
            },
          });
          break;
        }
      }
    } catch (err) {}
  };

  const handleInvoiceViewMore = () => {
    navigate("/management/listbooking/invoice");
  };

  // Handle flow
  const handleSelect = (view: string) => {
    setSelected(view);
    setDataState(initialDataState);
    if (view === "today") {
      handleTodayClick();
    } else if (view === "week") {
      handleThisWeekClick();
    } else if (view === "month") {
      handleThisMonthClick();
    }
  };

  // Multi button option
  const handleTodayClick = () => {
    const currentDate = new Date();
    allDashboardCount(formatDate(currentDate), formatDate(currentDate));
  };

  const handleThisWeekClick = () => {
    const currentDate = new Date();
    const dayOfWeek = currentDate.getDay();
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - dayOfWeek + 1); // Start on Monday (assuming 0 is Sunday)

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // End on Sunday

    allDashboardCount(formatDate(startOfWeek), formatDate(endOfWeek));
  };

  const handleThisMonthClick = () => {
    const currentDate = new Date();
    const startOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    );
    const endOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0
    );

    allDashboardCount(formatDate(startOfMonth), formatDate(endOfMonth));
  };

  const labelContent = (e: any) => `${e.dataItem.percentage} %`;

  // Checked in handle

  const handlePageChange = (pageState: any) => {
    setDataState({ ...dataState, ...pageState });
  };

  const CustomDateHeaderCell = (props: any) => {
    const formattedDate = headerFormatDate(props.date);
    return <div className="hourly-header-label">{formattedDate}</div>;
  };

  const headerFormatDate = (date: Date): string => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    // Get day of the week, day of the month, month, and year
    const day = String(date.getDate()).padStart(2, "0"); // Adds leading zero if necessary (e.g., "18")
    const month = months[date.getMonth()]; // E.g., "Sep"
    const year = date.getFullYear(); // E.g., "2024"

    // Concatenate in the format "EEE, dd-MMM-yyyy"
    return `${day}-${month}-${year}`;
  };

  const currentYear = new Date().getFullYear();
  const parseAdjust = (eventDate: any) => {
    const date = new Date(eventDate);
    date.setFullYear(currentYear);
    return date;
  };

  // Get Current date
  const getCurrentDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const [allSpaceHourlyData, setAllSpaceHourlyData] = useState([]);
  const [allSpaceTypeData, setAllSpaceTypeData] = useState([]);
  const [selectedSpaceTypeData, setSelectedBookingType] = useState();
  const [selectedDate, setSelectedDate] = useState(getCurrentDate());
  const [selectedSpaceId, setSelectedSpaceId] = useState();

  const hourlyBookingModelFields = {
    start: "Start",
    end: "End",
  };

  // Convert date format YYYY-MM-DD
  const formatDateToYYYYMMDD = (date: any) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // Hourly space list
  const getSpaceTypeAPI = async () => {
    try {
      const response = await axios.get(
        `${baseURL}odata/GetHourlyBookingSpaces`
      );
      if (response.data.content.length > 0) {
        setAllSpaceTypeData(response.data.content);
        setSelectedBookingType(response.data.content[0]);
        setSelectedSpaceId(response.data.content[0].id);
        getHourlyDataAPI(getCurrentDate(), response.data.content[0].id);
      } else {
        setAllSpaceTypeData([]);
      }
    } catch (err) {
      alert(err);
    }
  };

  // Hourly data
  const getHourlyDataAPI = async (date: any, id: any) => {
    try {
      const response = await axios.get(
        `${baseURL}odata/GetHourlyBookingsByDate?date=${date}&spaceId=${id}`
      );
      if (response.data.content.length > 0) {
        const sampleDataWithCustomSchema = response.data.content.map(
          (dataItem: any) => ({
            ...dataItem,
            AppointmentID: dataItem.id,
            title: dataItem.userName,
            Start: parseAdjust(dataItem.bookingFrom),
            End: parseAdjust(dataItem.bookingTo),
            isActive: false,
          })
        );
        setAllSpaceHourlyData(sampleDataWithCustomSchema);
      } else {
        setAllSpaceHourlyData([]);
      }
    } catch (err) {
      alert(err);
    }
  };

  // Handle hourly space id
  const handleChangeBookingType = (e: any) => {
    setSelectedBookingType(e.value);
    setSelectedSpaceId(e.value.id);
    getHourlyDataAPI(selectedDate, e.value.id);
  };

  const handleDateChange = (event: any) => {
    setSelectedDate(formatDateToYYYYMMDD(event.value));
    getHourlyDataAPI(formatDateToYYYYMMDD(event.value), selectedSpaceId);
  };

  const allSpaceCount = {
    totalSpaces: 10,
    durations: [
      { name: "Hourly", actualRevenue: 5000, targetRevenue: 7000 },
      { name: "Daily", actualRevenue: 4000, targetRevenue: 6000 },
      { name: "Monthly", actualRevenue: 3500, targetRevenue: 5000 },
      { name: "Quarterly", actualRevenue: 3000, targetRevenue: 4500 },
    ],
  };

  const [showDialog, setShowDialog] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState(null);

  // Function to open the dialog
  const handleViewMore = (spaceName: any) => {
    setSelectedSpace(spaceName);
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setSelectedSpace(null);
  };

  return (
    <div className="dashboard-bg">
      <div>
        <div className="dashboard-button">
          <Breadcrumbs breadCrumbData={breadCrumbsData} />
          <ButtonGroup>
            <Button
              selected={selected === "today"}
              onClick={() => {
                handleSelect("today");
              }}
              className={
                selected === "today" ? "selected-color" : "unselect-color"
              }
            >
              TODAY
            </Button>
            <Button
              selected={selected === "week"}
              onClick={() => handleSelect("week")}
              className={
                selected === "week" ? "selected-color" : "unselect-color"
              }
            >
              THIS WEEK
            </Button>
            <Button
              selected={selected === "month"}
              onClick={() => handleSelect("month")}
              className={
                selected === "month" ? "selected-color" : "unselect-color"
              }
            >
              THIS MONTH
            </Button>
          </ButtonGroup>
        </div>
        <hr className="hr-border" />
      </div>
      {/* Count view */}
      <div className="dashboard-grid">
        <div className="dashboard-item">
          <Card>
            <CardBody>
              <h3>Active Spaces</h3>
              <p>{allCount.totalSpaces}</p>
            </CardBody>
          </Card>
        </div>

        <div className="dashboard-item">
          <Card>
            <CardBody>
              <h3>Total Bookings</h3>
              <p>{allCount.totalBookings}</p>
            </CardBody>
          </Card>
        </div>

        <div className="dashboard-item">
          <Card>
            <CardBody>
              <h3>Checked In</h3>
              <p>{allCount.checkInCount}</p>
            </CardBody>
          </Card>
        </div>

        <div className="dashboard-item">
          <Card>
            <CardBody>
              <h3>Total Revenue</h3>
              <p>{allCount.totalRevenue}</p>
            </CardBody>
          </Card>
        </div>

        <div className="dashboard-item">
          <Card>
            <CardBody>
              <h3>Outstanding Amount</h3>
              <p>{allOutstandingList.outstandingPrice}</p>
            </CardBody>
          </Card>
        </div>

        <div className="dashboard-item">
          <Card>
            <CardBody>
              <h3>Amount in Due</h3>
              <p>{allOutstandingList.dueAmount}</p>
            </CardBody>
          </Card>
        </div>
      </div>
      {/* Checked-In and Payment Grid */}
      <div className="dashboard">
        <div className="grid-container">
          <div className="grid-section">
            <div className="header-row">
              <Label className="dashboard-header">Checked-in Customers</Label>
            </div>
            <Grid
              data={process(allCheckedList, dataState)}
              pageable={true}
              skip={dataState.skip}
              take={dataState.take}
              total={allCheckedList.length}
              onPageChange={(e) => setDataState(e.page)}
              onDataStateChange={handlePageChange}
            >
              <GridColumn
                field="id"
                title="S.No."
                width={0}
                headerCell={RightAlignedHeaderCell}
                cell={(props) => (
                  <td
                    style={{
                      height: "75px",
                      textAlign: "right",
                      paddingRight: "35px",
                    }}
                  >
                    {props.dataIndex + 1}
                  </td>
                )}
              />
              <GridColumn
                field="spaceTypeName"
                title="Space Type"
                cell={(props) => (
                  <td
                    style={{
                      height: "75px",
                      textAlign: "left",
                    }}
                  >
                    {props.dataItem.spaceTypeName}
                  </td>
                )}
              />
              <GridColumn
                field="durationName"
                title="Duration"
                cell={(props) => (
                  <td
                    style={{
                      height: "75px",
                      textAlign: "left",
                    }}
                  >
                    {props.dataItem.durationName}
                  </td>
                )}
              />
              <GridColumn
                field="customerName"
                title="Customer Name"
                cell={(props) => (
                  <td
                    style={{
                      height: "75px",
                      textAlign: "left",
                    }}
                  >
                    {props.dataItem.customerName}
                  </td>
                )}
              />
              <GridColumn
                title="Action"
                width={10}
                cell={(props) => (
                  <div className="common-action">
                    <MyViewCommandCell {...props} enterView={enterView} />
                  </div>
                )}
              />
            </Grid>
          </div>
          <div className="grid-section">
            <div className="header-row">
              <Label className="dashboard-header">Invoice Payments</Label>
              <Button
                onClick={handleInvoiceViewMore}
                className="view-more-button"
              >
                View More
              </Button>
            </div>
            <Grid data={allPaymentList}>
              <GridColumn
                field="id"
                title="S.No."
                width={10}
                headerCell={RightAlignedHeaderCell}
                cell={(props) => (
                  <td
                    style={{
                      height: "75px",
                      textAlign: "right",
                      paddingRight: "35px",
                    }}
                  >
                    {props.dataIndex + 1}
                  </td>
                )}
              />
              <GridColumn
                field="customerName"
                title="Customer Name"
                cell={(props) => (
                  <td
                    style={{
                      height: "75px",
                      textAlign: "left",
                    }}
                  >
                    {props.dataItem.customerName}
                  </td>
                )}
              />
              <GridColumn
                field="spaceTypeName"
                title="Space Type"
                cell={(props) => (
                  <td
                    style={{
                      height: "75px",
                      textAlign: "left",
                    }}
                  >
                    {props.dataItem.spaceTypeName}
                  </td>
                )}
              />
              <GridColumn
                field="paid"
                title="Paid (₹)"
                headerCell={RightAlignedHeaderCell}
                cell={(props) => (
                  <td
                    style={{
                      height: "75px",
                      textAlign: "right",
                      paddingRight: "55px",
                    }}
                  >
                    {props.dataItem.paid}
                  </td>
                )}
              />
              <GridColumn
                title="Action"
                width={10}
                cell={(props) => (
                  <div className="common-action">
                    <ViewDashboardPaymentCell
                      {...props}
                      paymentView={paymentView}
                    />
                  </div>
                )}
              />
            </Grid>
          </div>
        </div>
      </div>

      <div className="card-component" style={{ margin: "12px" }}>
        {/* Count view */}
        {/* <div className="dashboard-grid">
          <div className="dashboard-item">
            <Card>
              <CardBody>
                <h3>Space 1</h3>
                <div>
                  <div>
                    <p>Duration 1</p>
                  </div>
                  <div>
                    <p>
                      {allCount.totalSpaces} / {allCount.totalSpaces}
                    </p>
                  </div>
                  <div>
                    <p>Duration 2</p>
                  </div>
                  <div>
                    <p>
                      {allCount.totalSpaces} / {allCount.totalSpaces}
                    </p>
                  </div>
                  <div>
                    <p>...View More</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div> */}

        <div className="dashboard-grid">
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "60px",
              justifyContent: "center",
            }}
          >
            {Object.keys(allRevenueCountList ?? {}).map((spaceName, index) => (
              <Card
                key={index}
                style={{
                  minWidth: "200px",
                  padding: "20px",
                }}
              >
                <h6
                  style={{
                    color: "#42b677",
                    fontWeight: "bold",
                    paddingBottom: "8px",
                  }}
                >
                  {spaceName}
                </h6>
                <div>
                  {((allRevenueCountList ?? {})[spaceName] ?? [])
                    .slice(0, 2)
                    .map((duration, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "4px",
                        }}
                      >
                        <p style={{ fontWeight: "500" }}>
                          {duration.durationName}
                        </p>
                        <p style={{ margin: "10px" }}> </p>
                        <p style={{ marginLeft: "10px" }}>
                          {duration.actualRevenue} / {duration.targetedRevenue}
                        </p>
                      </div>
                    ))}
                  {(allRevenueCountList ?? {})[spaceName].length > 2 && (
                    <div style={{ textAlign: "right" }}>
                      <Button onClick={() => handleViewMore(spaceName)}>
                        VIEW MORE
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>

          {showDialog && selectedSpace && (
            <Dialog title={selectedSpace} onClose={handleCloseDialog}>
              <div>
                {(allRevenueCountList ?? {})[selectedSpace]?.map(
                  (duration, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "4px",
                      }}
                    >
                      <p style={{ fontWeight: "500" }}>
                        {duration.durationName}
                      </p>
                      <p style={{ margin: "10px" }}> </p>
                      <p style={{ marginLeft: "10px" }}>
                        {duration.actualRevenue} / {duration.targetedRevenue}
                      </p>
                    </div>
                  )
                )}
              </div>
              <DialogActionsBar>
                <Button onClick={handleCloseDialog}>Close</Button>
              </DialogActionsBar>
            </Dialog>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "20px",
          }}
        >
          <div>
            <Label className="dashboard-header">Hourly Booking</Label>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <Label
              style={{
                fontSize: "14px",
                fontWeight: "400",
                color: "#212121",
                marginRight: "10px",
                alignItems: "center",
              }}
            >
              Booking Type
            </Label>
            <DropDownList
              svgIcon={chevronDownIcon}
              name="spaceName"
              className="book-dropdown"
              data={allSpaceTypeData}
              textField="spaceName"
              dataItemKey="id"
              value={selectedSpaceTypeData}
              onChange={(e) => handleChangeBookingType(e)}
            />
          </div>
        </div>
        <Scheduler
          editable={false}
          modelFields={hourlyBookingModelFields}
          defaultDate={new Date()}
          data={allSpaceHourlyData}
          onDateChange={handleDateChange}
        >
          <DayView
            dateHeaderCell={CustomDateHeaderCell}
            showWorkHours={false}
            slotDivisions={4}
            startTime="08:00"
            endTime="22:00"
          />
        </Scheduler>
      </div>
      <div className="dashboard-chart report-top">
        <div className="chart-container report-chart">
          {/* Donut chart */}
          {/* <div className="chart-section">
            <Label className="dashboard-header">Invoices</Label>
            {allPaymentList.length > 0 ? (
              <div className="view-chart-top">
                <Card>
                  <Chart transitions={false}>
                    <ChartLegend position="bottom" />
                    <ChartSeries>
                      <ChartSeriesItem
                        color={commonChartColor}
                        type="donut"
                        data={allInvoiceList}
                        categoryField="category"
                        field="percentage"
                      >
                        <ChartSeriesLabels
                          color="#fff"
                          background="none"
                          content={labelContent}
                        />
                      </ChartSeriesItem>
                    </ChartSeries>
                  </Chart>
                </Card>
              </div>
            ) : (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                No Invoices data available
              </div>
            )}
          </div> */}
          {/* Pie chart */}
          <div className="chart-full-section">
            <Label className="dashboard-header">Occupancy (%)</Label>
            {allOccupancyList.length > 0 ? (
              <div className="view-chart-top">
                <Card>
                  <Chart transitions={false}>
                    <ChartLegend position="right" />
                    <ChartSeries>
                      <ChartSeriesItem
                        color={commonChartColor}
                        type="pie"
                        data={allOccupancyList}
                        field="percentage"
                        categoryField="spaceTypeName"
                        labels={{ visible: true }}
                      >
                        <ChartSeriesLabels
                          color="#000"
                          background="none"
                          content={labelContent}
                        />
                      </ChartSeriesItem>
                    </ChartSeries>
                  </Chart>
                </Card>
              </div>
            ) : (
              <div
                style={{
                  textAlign: "center",
                }}
              >
                No Occupancy data available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
